import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Ban,
  BookOpen,
  HandCoins,
  HeartHandshake,
  Landmark,
  Quote,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { GeometricPattern } from '@/layouts/PublicShell';
import { PageHero } from './shared';

/**
 * About the association and why it lends the way it does.
 *
 * Deliberately narrow in what it claims: the association's history is the
 * client's to write, so this page describes the lending principle — which is
 * documented and verifiable in the software itself — rather than inventing
 * membership figures or founding anecdotes.
 */
export default function About() {
  return (
    <>
      <PageHero
        eyebrow="About us"
        title="A foundation that lends without riba."
        description="AAF-UNIFEMGA is a loan scheme of the Aliu Abdulkabir Foundation, facilitated by the Obafemi Awolowo University Muslim Graduates' Association. The Foundation provides the funds; UNIFEMGA runs the scheme for its members. This platform exists to administer it honestly."
      />

      {/* Who does what. Two organisations sharing one scheme invites exactly
          this question, and leaving it unanswered is what makes a lender feel
          opaque. */}
      <section className="py-14 md:py-20">
        <div className="container">
          <div className="mx-auto mb-10 max-w-2xl text-center">
            <p className="eyebrow mb-3">Two bodies, one scheme</p>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground md:text-3xl">
              Who is behind this.
            </h2>
          </div>

          <div className="mx-auto grid max-w-4xl gap-6 md:grid-cols-2">
            <div className="rounded-2xl border border-border bg-card p-7">
              <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Landmark size={20} />
              </span>
              <p className="eyebrow mb-2">The lender</p>
              <h3 className="mb-3 font-heading text-lg font-bold text-card-foreground">
                Aliu Abdulkabir Foundation
              </h3>
              <p className="text-sm leading-relaxed text-foreground-secondary">
                The Foundation provides the money that is lent. Because every loan is qard hasan,
                it takes back exactly what it puts out — no interest, no fee, no share of anything.
                What it gets in return is the reward of the loan itself.
              </p>
            </div>

            <div className="rounded-2xl border border-border bg-card p-7">
              <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Users size={20} />
              </span>
              <p className="eyebrow mb-2">The facilitator</p>
              <h3 className="mb-3 font-heading text-lg font-bold text-card-foreground">
                UNIFEMGA
              </h3>
              <p className="text-sm leading-relaxed text-foreground-secondary">
                The Obafemi Awolowo University Muslim Graduates&rsquo; Association runs the scheme:
                it confirms who is a member, reviews applications, telephones guarantors and keeps
                the records. It is the association you deal with day to day.
              </p>
            </div>
          </div>

          <p className="mx-auto mt-8 max-w-2xl text-center text-sm leading-relaxed text-foreground-secondary">
            In short: the Foundation funds it, UNIFEMGA administers it, and you repay exactly what
            you borrowed.
          </p>
        </div>
      </section>

      {/* What Qard Hasan is */}
      <section className="py-14 md:py-20">
        <div className="container">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <p className="eyebrow mb-3">The principle</p>
              <h2 className="mb-5 font-heading text-2xl font-bold tracking-tight text-foreground md:text-3xl">
                Qard Hasan, in plain terms.
              </h2>
              <div className="space-y-4 leading-relaxed text-foreground-secondary">
                <p>
                  <em>Qard hasan</em> means a benevolent loan: money lent for the borrower&rsquo;s
                  benefit, repaid in the amount that was lent and nothing more. It is the only form
                  of lending Islamic law permits between people, because any increase stipulated on
                  a loan is riba.
                </p>
                <p>
                  For UNIFEMGA that is not a marketing position that could be revised next year. It
                  is the reason the lending scheme exists at all, and it is why this software was
                  built the way it was.
                </p>
              </div>
            </div>

            <figure className="relative overflow-hidden rounded-2xl border border-border bg-card p-7 sm:p-9">
              <GeometricPattern className="pointer-events-none absolute -right-12 -top-12 h-48 w-48 text-primary/[0.06]" />
              <Quote className="relative mb-4 text-primary/30" size={30} />
              <blockquote className="relative font-heading text-lg leading-relaxed text-foreground">
                &ldquo;Who is it that will lend to Allah a goodly loan, so He may multiply it for
                him, and he will have a noble reward?&rdquo;
              </blockquote>
              <figcaption className="relative mt-4 text-sm font-semibold text-foreground-secondary">
                Qur&rsquo;an 57:11
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* Built into the software */}
      <section className="border-y border-border bg-background-alt py-14 md:py-20">
        <div className="container">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <p className="eyebrow mb-3">How we keep the promise</p>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground md:text-3xl">
              The rule is in the structure, not the policy.
            </h2>
            <p className="mt-3 leading-relaxed text-foreground-secondary">
              A promise not to charge interest is only as good as whoever is holding the switch.
              Here there is no switch.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                icon: Ban,
                title: 'No field to charge it',
                body: 'The database has no interest, fee or penalty column on any loan, schedule or repayment. There is nowhere for a charge to be recorded, so none can be.',
              },
              {
                icon: ShieldCheck,
                title: 'Totals recomputed, never incremented',
                body: 'What you owe is derived from your repayment schedule each time it changes. A balance cannot quietly grow between repayments.',
              },
              {
                icon: BookOpen,
                title: 'An append-only audit trail',
                body: 'Every decision — approvals, repayments, corrections — is written in the same transaction as the change itself, and nothing in the system can edit or delete it.',
              },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-2xl border border-border bg-card p-7">
                <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon size={20} />
                </div>
                <h3 className="mb-2.5 font-heading text-base font-bold text-card-foreground">
                  {title}
                </h3>
                <p className="text-sm leading-relaxed text-foreground-secondary">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What replaces interest */}
      <section className="py-14 md:py-20">
        <div className="container">
          <div className="mx-auto max-w-3xl">
            <p className="eyebrow mb-3">The honest part</p>
            <h2 className="mb-5 font-heading text-2xl font-bold tracking-tight text-foreground md:text-3xl">
              If there is no interest, what protects the fund?
            </h2>
            <div className="space-y-4 leading-relaxed text-foreground-secondary">
              <p>
                Conventional lenders price risk into a rate: the more likely you are to default, the
                more you pay. Remove the rate and something else has to do that work. Here it is
                three things, and none of them cost you money.
              </p>
            </div>

            <div className="mt-8 space-y-4">
              {[
                {
                  icon: Users,
                  title: 'Guarantors who are telephoned',
                  body: 'You name members who will stand for you, and an officer calls each one to confirm before anything is approved. That call is the credit check.',
                },
                {
                  icon: HeartHandshake,
                  title: 'A conversation when you fall behind',
                  body: 'Arrears bring a phone call, not a charge. Most difficulty is temporary and a rescheduled plan resolves it.',
                },
                {
                  icon: HandCoins,
                  title: 'Access, not money, is what is at stake',
                  body: 'The consequence of a default is a suspension from borrowing again — for you and, where relevant, for those who guaranteed you. You never owe more than you took.',
                },
              ].map(({ icon: Icon, title, body }) => (
                <div
                  key={title}
                  className="flex gap-4 rounded-2xl border border-border bg-card p-5 sm:p-6"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Icon size={18} />
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-heading text-base font-bold text-card-foreground">
                      {title}
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-secondary">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-background-alt py-14 md:py-16">
        <div className="container flex flex-col items-center gap-6 text-center">
          <h2 className="font-heading text-2xl font-bold text-foreground md:text-3xl">
            Questions before you apply?
          </h2>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link
              to="/faq"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-7 py-3.5 font-bold text-primary-foreground shadow-brand transition-transform hover:-translate-y-0.5"
            >
              Read the FAQs <ArrowRight size={17} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-xl border-2 border-border px-7 py-3.5 font-bold text-foreground transition-colors hover:border-primary hover:text-primary"
            >
              Contact us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
